import { useEffect, useState, type ReactNode } from "react";
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { bucketLabel, type DashboardResponse, type Period, type WealthPoint } from "@/lib/dashboard";
import { money } from "@/lib/format";

const PERIODS: [Period, string][] = [["monthly", "Mes"], ["quarterly", "Trim"], ["yearly", "Año"]];
const PERIOD_WORDS: Record<Period, string> = { monthly: "en los últimos 12 meses", quarterly: "en los últimos 8 trimestres", yearly: "en los últimos 5 años" };
const POS = { line: "#16294D", fill: "#DFE7F3" };
const NEG = { line: "#9C3D24", fill: "#F3DED6" };
const Amt = ({ v, bad }: { v: number; bad?: boolean }) => <b className={`tabular font-semibold ${bad ? "text-[#9C3D24]" : "text-[#2C4F91]"}`}>{money(Math.abs(v))}</b>;

function ticksOf(labels: string[]): string[] {
  if (labels.length <= 5) return labels;
  const step = Math.ceil((labels.length - 1) / 4);
  return labels.filter((_, i) => (labels.length - 1 - i) % step === 0);
}

function PeriodPill({ value, onChange, id }: { value: Period; onChange: (p: Period) => void; id: string }) {
  return (
    <div className="flex shrink-0 rounded-full border border-[#E7DECF] bg-white p-0.5" data-testid={`${id}-period`}>
      {PERIODS.map(([p, l]) => (
        <button key={p} onClick={() => onChange(p)} data-testid={`${id}-period-${p}`}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${value === p ? "bg-[#16294D] text-white" : "text-slate-500 hover:text-[#16294D]"}`}>{l}</button>
      ))}
    </div>
  );
}

function Spark({ data, id }: { data: { label: string; value: number }[]; id: string }) {
  const values = data.map((p) => p.value);
  const max = Math.max(...values, 0), min = Math.min(...values, 0);
  const crosses = values.some((v) => v < 0) && values.some((v) => v > 0);
  const tone = values.length && values[values.length - 1] < 0 && !crosses ? NEG : POS;
  const allNeg = values.length > 0 && values.every((v) => v <= 0) && values.some((v) => v < 0);
  const c = allNeg ? NEG : tone;
  const split = crosses ? max / (max - min) : 0;
  const lastIdx = data.length - 1;
  const lastColor = (values[lastIdx] ?? 0) < 0 ? NEG.line : POS.line;
  const gid = `${id}-grad`;
  return (
    <div className="mt-5 h-[130px]" data-testid={`${id}-chart`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 22, left: 22, bottom: 0 }}>
          <defs>
            <linearGradient id={`${gid}-line`} x1="0" y1="0" x2="0" y2="1">
              <stop offset={split} stopColor={POS.line} /><stop offset={split} stopColor={NEG.line} />
            </linearGradient>
            <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset={split} stopColor={POS.fill} /><stop offset={split} stopColor={NEG.fill} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[(lo: number) => lo - Math.abs(lo) * 0.04, (hi: number) => hi + Math.abs(hi) * 0.04]} />
          <XAxis dataKey="label" ticks={ticksOf(data.map((p) => p.label))} interval={0} tick={{ fontSize: 11, fill: "#5A6170" }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v) => [money(Number(v)), ""]} separator="" contentStyle={{ borderRadius: 10, border: "1px solid #DDE1E8", fontSize: 12 }} />
          {crosses && <ReferenceLine y={0} stroke="#CBB898" strokeWidth={1} data-testid={`${id}-zero-line`} />}
          <Area type="monotone" dataKey="value" baseValue={crosses ? 0 : "dataMin"} isAnimationActive={false}
            stroke={crosses ? `url(#${gid}-line)` : c.line} strokeWidth={2.5}
            fill={crosses ? `url(#${gid}-fill)` : c.fill} fillOpacity={1}
            dot={(p: { index?: number; cx?: number; cy?: number }) => p.index === lastIdx
              ? <circle key="last" cx={p.cx} cy={p.cy} r={5} fill={lastColor} stroke="#FAF7F1" strokeWidth={2} />
              : <g key={p.index} />} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Breakdown({ d }: { d: DashboardResponse }) {
  const bank = d.liquidity.bank, saved = d.liquidity.savings_investment;
  const goods = Math.round((d.assets_total - bank - saved) * 100) / 100;
  const parts: [string, string, number, string][] = [
    ["networth-bank", "A la mano: cuentas y efectivo", bank, "#16294D"],
    ["networth-invested", "Trabajando: inversiones", saved, "#7F97C4"],
    ["networth-goods", "Bienes: casa, terreno, auto", goods, "#CBB898"],
  ];
  const base = parts.reduce((s, p) => s + Math.max(p[2], 0), 0);
  return (
    <div className="rounded-2xl border border-[#ECE3D3] bg-white px-4 py-4" data-testid="networth-breakdown">
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[#ECE3D3]">
        {base > 0 && parts.map(([id, , v, c]) => v > 0 && <div key={id} style={{ width: `${(v / base) * 100}%`, background: c }} />)}
      </div>
      <div className="mt-4 space-y-2 text-sm text-slate-600">
        {parts.map(([id, l, v, c]) => (
          <div key={id} className="flex justify-between gap-3" data-testid={id}>
            <span className="flex min-w-0 items-start gap-2"><i className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c }} /><span className="leading-snug">{l}</span></span>
            <span className="tabular font-semibold text-[#16294D]">{money(v)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-3 border-t border-[#ECE3D3] pt-2.5" data-testid="networth-debts">
          <span>Menos lo que debes</span>
          <span className={`tabular font-semibold ${d.liabilities_total > 0 ? "text-[#9C3D24]" : "text-[#16294D]"}`}>{money(-d.liabilities_total)}</span>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, tone, id }: { label: string; value: number; tone: string; id: string }) {
  return (
    <div className="rounded-2xl border border-[#ECE3D3] bg-white px-4 py-3" data-testid={id}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`tabular mt-0.5 font-semibold ${tone}`}>{money(value)}</p>
    </div>
  );
}

interface CardProps { id: string; label: string; period: Period; onPeriod: (p: Period) => void; value: number; phrase: ReactNode; chart: { label: string; value: number }[]; minis: ReactNode }

function Card({ id, label, period, onPeriod, value, phrase, chart, minis }: CardProps) {
  return (
    <div className="rounded-[24px] border border-[#E7DECF] bg-[#FAF7F1] p-5 sm:p-7" data-testid={id}>
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <p className="truncate text-xs font-bold uppercase tracking-[0.14em] text-[#7A6548]">{label}</p>
        <PeriodPill value={period} onChange={onPeriod} id={id} />
      </div>
      <p className={`mt-3 font-['Lora'] text-[34px] font-semibold leading-tight tabular-nums ${value < 0 ? "text-[#9C3D24]" : "text-[#16294D]"}`} data-testid={`${id}-value`}>{money(value)}</p>
      <p className="mt-2 text-sm text-slate-600" data-testid={`${id}-phrase`}>{phrase}</p>
      <Spark data={chart} id={id} />
      <div className="mt-5">{minis}</div>
    </div>
  );
}

function netPhrase(now: number, change: number, period: Period, reval: number, months: number | null): ReactNode {
  const when = PERIOD_WORDS[period];
  if (now >= 0) {
    if (change < 0) return <>Bajó <Amt v={change} bad /> {when}. Vale la pena revisar qué pasó.</>;
    if (change > 0 && reval > change / 2) return <>Creció <Amt v={change} /> {when}, sobre todo por el valor de tus bienes. Ese crecimiento no es dinero disponible.</>;
    if (months !== null && months < 3) return <>Creció <Amt v={change} /> {when}. Tu colchón todavía es chico; vale la pena fortalecerlo.</>;
    return <>Creció <Amt v={change} /> {when}. Tu dinero está trabajando.</>;
  }
  return change > 0
    ? <>Mejoró <Amt v={change} /> {when}. Todavía debes más de lo que tienes, y eso se puede voltear.</>
    : <>Bajó <Amt v={change} bad /> {when}. Tus deudas crecen más rápido que lo que tienes.</>;
}

function coverPhrase(months: number | null): ReactNode {
  if (months === null) return "Aún no hay gastos de los últimos 3 meses para calcular cuánto te dura.";
  const blue = (t: string) => <b className="tabular font-semibold text-[#2C4F91]">{t}</b>;
  if (months < 1) {
    const w = Math.max(0, Math.round(months * 52 / 12));
    return <>Te alcanza para {blue(`${w} ${w === 1 ? "semana" : "semanas"}`)} de tus gastos. Construir un colchón es tu prioridad.</>;
  }
  const m = Math.round(months);
  const t = blue(`${m} ${m === 1 ? "mes" : "meses"}`);
  if (m >= 6) return <>Equivale a {t} de tus gastos. Tienes un colchón sólido.</>;
  if (m >= 3) return <>Equivale a {t} de tus gastos. Vas bien; la meta son 6.</>;
  return <>Equivale a {t} de tus gastos. Un imprevisto te pondría en aprietos.</>;
}

export function WealthCards({ d }: { d: DashboardResponse }) {
  const [period, setPeriod] = useState<Period>("monthly");
  const [w, setW] = useState<WealthPoint[]>([]);
  const [reval, setReval] = useState(0);
  useEffect(() => {
    api.get<{ points: WealthPoint[]; revaluation_in_period: number }>(`/dashboard/wealth?period=${period}`)
      .then(({ data }) => { setW(data.points); setReval(data.revaluation_in_period); })
      .catch((err) => toast.error(formatApiError(err)));
  }, [period, d]);
  const label = (k: string) => bucketLabel(k, period);
  const change = w.length ? w[w.length - 1].net_worth - w[0].net_worth : 0;
  const L = d.liquidity;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card id="networth-card" label="Tu patrimonio" period={period} onPeriod={setPeriod} value={d.net_worth} phrase={netPhrase(d.net_worth, change, period, reval, L.months_covered)}
        chart={w.map((p) => ({ label: label(p.key), value: p.net_worth }))}
        minis={<Breakdown d={d} />} />
      <Card id="liquidity-card" label="Tu liquidez" period={period} onPeriod={setPeriod} value={L.total} phrase={coverPhrase(L.months_covered)}
        chart={w.map((p) => ({ label: label(p.key), value: p.liquid }))}
        minis={<div className="grid grid-cols-2 gap-3"><Mini id="liquidity-bank" label="A la mano" value={L.bank} tone="text-[#16294D]" /><Mini id="liquidity-saved" label="Trabajando" value={L.savings_investment} tone="text-[#16294D]" /></div>} />
    </div>
  );
}
